import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAppDispatch } from "@/redux/store";
import {
  setUser,
  setLoading,
  logout as logoutAction,
  setPaymentRequired,
  type User,
} from "@/redux/slices/authSlice";
import { authAPI } from "@/lib/services/api.service";
import { sessionTracker } from "@/lib/services/session.service";
import { getSupabaseBrowserClient } from "@/lib/supabaseBrowser";
import { clearPartialMfaSessionIfOnAppRoute } from "@/lib/supabaseFactorAuth";

type SessionLoginResponse = {
  user: Omit<User, "company" | "needsCompanySetup">;
  company: User["company"] | null;
  session: { access_token: string; refresh_token: string };
  paymentRequired: boolean;
  onboardingRequired: boolean;
  needsCompanySetup: boolean;
};

export const useAuthActions = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const applySessionLoginResult = useCallback(
    (data: SessionLoginResponse, rememberMe: boolean) => {
      if (data.session) {
        const primaryStorage = rememberMe ? localStorage : sessionStorage;
        const secondaryStorage = rememberMe ? sessionStorage : localStorage;
        primaryStorage.setItem("sb-access-token", data.session.access_token);
        primaryStorage.setItem("sb-refresh-token", data.session.refresh_token);
        secondaryStorage.removeItem("sb-access-token");
        secondaryStorage.removeItem("sb-refresh-token");
      }

      dispatch(
        setUser({
          ...data.user,
          company: data.company ?? undefined,
          needsCompanySetup: !!data.needsCompanySetup,
        })
      );
      dispatch(setPaymentRequired(!!data.paymentRequired));

      sessionTracker.start().catch((err) => {
        console.warn("Failed to start session tracking:", err);
      });

      if (data.needsCompanySetup || data.onboardingRequired) {
        navigate("/onboarding", { replace: true });
      } else if (data.paymentRequired) {
        navigate("/payment", { replace: true });
      } else {
        navigate("/", { replace: true });
      }
    },
    [dispatch, navigate]
  );

  /**
   * Google / GitHub / enterprise SSO — after `exchangeCodeForSession` or `setSession`,
   * syncs server cookies and Redux the same way as email login.
   */
  const completeOAuthSession = useCallback(
    async (
      session: {
        access_token: string;
        refresh_token: string;
        expires_at?: number;
        expires_in?: number;
      },
      rememberMe = true
    ) => {
      try {
        dispatch(setLoading(true));
        const data = await authAPI.syncSession({
          access_token: session.access_token,
          refresh_token: session.refresh_token,
          rememberMe,
          expires_at: session.expires_at,
          expires_in: session.expires_in,
        });
        applySessionLoginResult(data, rememberMe);
        return { success: true as const };
      } catch (error) {
        return { success: false as const, error };
      } finally {
        dispatch(setLoading(false));
      }
    },
    [dispatch, applySessionLoginResult]
  );

  const logout = useCallback(async () => {
    try {
      // Stop session tracking first
      await sessionTracker.stop();

      try {
        await getSupabaseBrowserClient().auth.signOut();
      } catch {
        /* ignore */
      }

      await authAPI.logout();
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      // Clear tokens
      localStorage.removeItem("sb-access-token");
      localStorage.removeItem("sb-refresh-token");
      localStorage.removeItem("accessToken");
      localStorage.removeItem("refreshToken");
      sessionStorage.removeItem("sb-access-token");
      sessionStorage.removeItem("sb-refresh-token");
      sessionStorage.removeItem("accessToken");
      sessionStorage.removeItem("refreshToken");

      dispatch(logoutAction());
      navigate("/login");
    }
  }, [dispatch, navigate]);

  const checkAuth = useCallback(async () => {
    try {
      dispatch(setLoading(true));
      await clearPartialMfaSessionIfOnAppRoute();
      const { user, company, paymentRequired, onboardingRequired, needsCompanySetup } =
        await authAPI.getMe();
      // Merge company data into user object for Redux state
      dispatch(
        setUser({
          ...user,
          company,
          needsCompanySetup: !!needsCompanySetup,
        })
      );
      dispatch(setPaymentRequired(!!paymentRequired));

      // Start session tracking if not already tracking
      if (!sessionTracker.isActive) {
        sessionTracker.start().catch((err) => {
          console.warn("Failed to start session tracking:", err);
        });
      }

      const currentPath = window.location.pathname;
      if (needsCompanySetup && currentPath !== "/onboarding") {
        navigate("/onboarding", { replace: true });
      } else if (!needsCompanySetup && onboardingRequired && currentPath !== "/onboarding") {
        // Send users to onboarding from app routes when onboarding is incomplete.
        const stayOnThesePaths = [
          "/login",
          "/register",
          "/verify-email",
          "/forgot-password",
          "/reset-password",
          "/auth/callback",
        ];
        if (!stayOnThesePaths.includes(currentPath)) {
          navigate("/onboarding", { replace: true });
        }
      }

      return { success: true, user, paymentRequired, onboardingRequired, needsCompanySetup };
    } catch (error: unknown) {
      // Check for auth errors
      const statusCode =
        (error as { response?: { status?: number; data?: { statusCode?: number } } })?.response
          ?.status ||
        (error as { response?: { data?: { statusCode?: number } } })?.response?.data?.statusCode;

      if (statusCode === 401 || statusCode === 403) {
        const code = (error as { response?: { data?: { code?: string } } })?.response?.data?.code;
        if (code === "AUTH_MFA_REQUIRED") {
          try {
            await getSupabaseBrowserClient().auth.signOut();
          } catch {
            /* ignore */
          }
        }
        // Auth failed - clear tokens
        localStorage.removeItem("sb-access-token");
        localStorage.removeItem("sb-refresh-token");
        localStorage.removeItem("accessToken");
        localStorage.removeItem("refreshToken");
        sessionStorage.removeItem("sb-access-token");
        sessionStorage.removeItem("sb-refresh-token");
        sessionStorage.removeItem("accessToken");
        sessionStorage.removeItem("refreshToken");
        dispatch(logoutAction());
      }

      return { success: false };
    } finally {
      dispatch(setLoading(false));
    }
  }, [dispatch, navigate]);

  return {
    completeOAuthSession,
    logout,
    checkAuth,
  };
};
